<template>
	<div class="sb-container">
		<div class="sb-wrapper" :key="refresh">
			<!-- Loader -->
			<div class="loader-container" v-if="pageLoader">
				<RotatingLoader :size="30" :speed="0.6" />
				<span>Loading</span>
			</div>

			<!-- Header -->
			<div v-else class="sb-header">
				<div v-if="!isCompleted && currentStage != 0" class="sb-header-completed">
					<div class="back-btn-wrap">
						<button class="back-btn" @click="goHome()">
							<img :src="require('@/assets/icons/back-arrow.svg')" alt="">
						</button>
						<span class="home-btn-title">Storybank Details</span>
					</div>

					<div class="button-container">
						<button class="share-btn" @click="showShareMenu = true">
							<div class="completed-wrappershare">
								<img :src="require('@/assets/icons/share.svg')" />
							</div>
						</button>
						<button class="generate-btn" :disabled="downloadLoader" @click="downloadFinalStorybank">
							<div class="continue-btn">
								<Svg :src="require('@/assets/icons/download.svg')" fill="white"></Svg>
								<p>Download</p>
								<v-progress-circular v-if="downloadLoader" indeterminate :size="18"
									:width="2"></v-progress-circular>
							</div>
						</button>
					</div>
				</div>

				<div v-else :class="['sb-header-wrapper', { 'completed-header': isCompleted }]">
					<button v-if="!isCompleted && currentStage == 0" class="home-btn" @click="popupType = 'goHome'">
						<img :src="require('@/assets/icons/home.svg')" alt="">
					</button>
					<div class="header-title-wrapper">
						<div class="header-title">
							<span class="doc-title">{{ sbTitle }}</span>
							<RenamePopup v-if="isEditingTitle" :docName="sbTitle" :isEditing="isEditingTitle"
								:id="$route.params.storyBankId" flowType="storyBank"
								@update-isEdit="isEditingTitle = $event" @updated-prTitle="sbTitle = $event" />
							<button class="edit-btn" @click="isEditingTitle = true">
								<img :src="require('@/assets/icons/edit.svg')" />
							</button>
						</div>
					</div>
				</div>
			</div>

			<!-- Stage Content -->
			<div v-if="currentStage == 0" class="stage-container">
				<SourceDocument :sourceList="sourceList" flowType="Storybank" :documentId="documentId"
					:documentType="documentType" @document-meta-selected="handleSelectedDocument" />
			</div>

			<div v-else class="completed-section" v-if="storyBank">
				<SBCompleted :storyBank="storyBank" :key="refresh" :isCompleted="isCompleted" v-model="sbTitle" />
			</div>

			<!-- Footer -->
			<div v-if="currentStage == 0" class="sb-footer">
				<div class="footer-left">
					<button class="delete-btn" @click="popupType = 'delete'">
						<img :src="require('@/assets/icons/delete.svg')" />
						<v-progress-circular v-if="deleteLoader" indeterminate :size="18"
							:width="2"></v-progress-circular>
					</button>
					<button class="save-as-draft" @click="popupType = 'saveAsDraft'"
						:disabled="selectedDocument == null">
						<img :src="require('@/assets/icons/save.svg')" />
						<p>Save as Draft</p>
					</button>
				</div>
				<div class="footer-left">
					<button class="generate-btn" @click="handleGenerateClick()" :disabled="selectedDocument == null">
						<p> Generate Storybank</p>
						<img :src="require('@/assets/icons/next.svg')" alt="" />
						<v-progress-circular v-if="nextStageLoader" indeterminate :size="18"
							:width="2"></v-progress-circular>
					</button>
				</div>
			</div>
		</div>

		<!-- Popups -->
		<ConfirmPopup v-if="confirmPopup" v-model="confirmPopup" :title="popupData.title" :image="popupData.image"
			:type="popupData.type" />
		<CommonPopup v-if="showCommonPopup" :showCommonPopup="showCommonPopup" :popupType="popupType"
			@confirm-action="handlePopupConfirmation" />
		<v-overlay v-if="showOverlayLoader" v-model="showOverlayLoader" class="overlay" persistent="true">
			<RotatingLoader :size="30" :speed="0.6" />
			<span>Generating</span>
		</v-overlay>
	</div>
</template>

<script>
import axiosInstance from '@/axiosInterceptor';
import CommonPopup from '@/components/CommonPopup.vue';
import { Eventbus } from '@/plugins/eventBus';
import SourceDocument from '@/components/SourceDocument.vue';
import RenamePopup from '@/components/RenamePopup.vue';
import RotatingLoader from '@/components/CircularProgressBar.vue';
import SBCompleted from '@/views/StoryBank/StoryBankCompleted.vue';
import ConfirmPopup from '@/components/ConfirmPopup.vue';
import { downloadPr } from '@/helper/downloadPr';

export default {
	name: 'StoryBankDetails',
	components: {
		CommonPopup, SourceDocument, RenamePopup, RotatingLoader, ConfirmPopup, SBCompleted
	},
	data() {
		return {
			pageLoader: false,
			currentStage: null,
			popupType: null,
			documentType: null,
			documentId: null,
			storyBank: null,
			confirmPopup: false,
			selectedDocument: null,
			saveDraftLoader: false,
			sbTitle: '',
			isEditingTitle: false,
			sourceList: [],
			refresh: 0,
			deleteLoader: false,
			nextStageLoader: false,
			isCompleted: false,
			downloadLoader: false,
			showOverlayLoader: false,
			showCommonPopup: false
		}
	},
	watch: {
		popupType() {
			if (this.popupType) {
				this.showCommonPopup = true;
			}
		},
		storyBank: {
			handler(newVal) {
			},
			immediate: true,
			deep: true
		}
	},
	methods: {
		goHome() {
			this.$store.dispatch('updateIsGeneratePopup', false);
			this.$router.push({ name: 'storyBankHome' });
		},
		async downloadFinalStorybank() {
			this.downloadLoader = true;
			const payload = { storyBankId: this.$route.params.storyBankId, flowType: "STORY_BANK" };
			await downloadPr(payload, this.sbTitle);
			this.downloadLoader = false;
		},
		handleSelectedDocument(doc) {
			this.documentType = doc.sourceType;
			this.documentId = doc.documentId;
			this.selectedDocument = doc.selectedDocument;
		},
		async handlePopupConfirmation(val) {
			this.showCommonPopup = false;
			if (val === 'yes') {
				if (this.popupType === 'delete') {
					axiosInstance.put('/storyBank/delete', { storyBankId: this.$route.params.storyBankId })
						.then((response) => {
							if (response.status == 200) {
								this.popupData = {
									title: 'Storybank Discarded',
									image: require('@/assets/icons/delete_red.svg'),
									type: 'success'
								};
								this.confirmPopup = true;
								setTimeout(() => {
									this.confirmPopup = false;
									this.$router.push({ name: 'storyBankHome' });
								}, 2000);
							}

						})
						.catch((error) => {
							Eventbus.emit('commonSnackbar', {
								snackType: 'error',
								message: 'Error deleting storybank',
							});
						})
				}
				else if (this.popupType === 'saveAsDraft') {
					const storyBankId = this.$route.params.storyBankId;
					const payload = {
						documentType: this.documentType,
						documentId: this.documentId,
						storyBankId: storyBankId

					}
					this.saveDraftLoader = true;
					const response = await axiosInstance.put('/storyBank/saveDraft', payload);
					if (response.status == 200) {
						this.popupData = {
							title: 'Storybank saved as Draft',
							image: require('@/assets/icons/success.svg'),
							type: 'success'
						};
						this.confirmPopup = true;
						setTimeout(() => {
							this.confirmPopup = false;
							this.$router.push({ name: 'storyBankHome' });
						}, 2000);

					}
					this.saveDraftLoader = false;
				}
				else {
					this.$store.dispatch('updateIsGeneratePopup', false);
					this.$router.push({ name: 'storyBankHome' });
				}
			}
			this.popupType = null;
		},
		async handleGenerateClick() {
			this.showOverlayLoader = true;
			try {
				const payload = { documentType: this.documentType, documentId: this.documentId, storyBankId: this.$route.params.storyBankId };
				await axiosInstance.put('/storyBank/saveDraft', payload);
				const response = await axiosInstance.post('/storyBank/generate', payload);
				if (response.status === 200) {
					this.isCompleted = true;
					this.getStorybankById();
					this.currentStage += 1;
					this.refresh += 1;
					this.showOverlayLoader = false;
				}
			} catch (error) {
				console.error("Error generating Storybank", error);
				if (error.response?.status === 504 || error.code === 'ERR_NETWORK') {
                    let attempts = 0;
                    let maxAttempts = 2;
                    const intervalTime = 15000;
                    const intervalId = setInterval(async () => {
                        attempts++;
                        try {
                            let isFallback = true;
                            const plan = await this.getStorybankById(isFallback);
                            if (this.storyBank != null) {
                                clearInterval(intervalId);
                                this.currentStage += 1;
                                this.refresh += 1;
                                this.showOverlayLoader = false
                            } else if (attempts >= maxAttempts) {
                                clearInterval(intervalId);
                                Eventbus.emit('commonSnackbar', {
                                    snackType: 'warning',
                                    message: 'Storybank generation is taking longer than expected. Please check again later.',
                                });
                            }
                        } catch (pollError) {
                            console.error("Error during polling:", pollError);
                            this.showOverlayLoader = false
                            clearInterval(intervalId);
                            Eventbus.emit('commonSnackbar', {
                                snackType: 'error',
                                message: 'Failed to generate Storybank. Please try again later.',
                            });

                        }
                    }, intervalTime);
                }
			}
		},
		async getStorybankById() {
			this.pageLoader = true;
			const sbId = this.$route.params.storyBankId;
			await axiosInstance.get(`/storyBank/${sbId}`)
				.then(response => {
					if (response.status === 200) {
						const data = response.data.data;
						this.documentId = data.documentId;
						this.documentType = data.documentType;
						this.sbTitle = data.title;
						this.storyBank = data.storyBankContent;						
						this.currentStage = data.storyBankContent ? 1 : 0;
					}
				})
				.finally(() => { this.pageLoader = false; });
		},
		async getDocuments() {
			const response = await axiosInstance.get('/document');
			if (response.status === 200) this.sourceList = response.data.data;
		}
	},
	mounted() {
		this.isCompleted = this.$store.getters.getIsGeneratePopup;
		this.getStorybankById();
		this.getDocuments();
	}
}
</script>

<style scoped>
.sb-container {
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	background: var(--dark-background);
}

.sb-wrapper {
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
}

.sb-header {
	position: sticky;
	top: 0;
	background: #FFFFFF14;
	color: white;
	z-index: 100;
	padding: 1rem 1.5rem;
}

.sb-header-completed {
	display: flex;
	justify-content: space-between;
	gap: 4.5rem;
}

.sb-header-wrapper {
	width: 54%;
	display: flex;
	justify-content: space-between;
	gap: 4.5rem;
}

.sb-footer {
	position: sticky;
	bottom: 0;
	display: flex;
	justify-content: space-between;
	padding: 1rem 2rem;
	background: #180914;
	border-top: 1px solid #FFFFFF33;
	color: white;
	z-index: 100;
}

.pr-plan-container {
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	width: 100%;
	height: 100%;
	background: var(--dark-background);
	background-size: 100% 100%;
}

.pr-plan-wrapper {
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
}

.no-data {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	width: 100%;
}

.pr-plan-header {
	position: sticky;
	top: 0;
	right: 0;
	left: 0;
	display: flex;
	flex-direction: column;
	justify-content: center;
	background: #FFFFFF14;
	color: white;
	z-index: 100;
	padding: 1rem 1.5rem;
}


.pr-plan-header-completed {
	display: flex;
	justify-content: space-between;
	gap: 4.5rem;
}

.back-btn-wrap {
	display: flex;
	flex-direction: row;
	gap: 1rem;
	justify-content: center;
	align-items: center;
}

.back-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	background: #FFFFFFFF;
	border-radius: 50%;
	height: 2.5rem;
	width: 2.5rem;
}

.home-btn-title {
	font-weight: 500;
	text-align: center;
	font-size: 18px;
}


.pr-plan-header-wrapper {
	width: 54%;
	display: flex;
	justify-content: space-between;
	gap: 4.5rem;
}

.completed {
	width: 100%;
}

.completed-header {
	width: 100%;
	display: flex;
	justify-content: center;
	gap: 4.5rem;
}

.home-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	background: #FFFFFF1F;
	border-radius: 8px;
	padding: 10px;
}

.header-title-wrapper {
	display: flex;
	justify-content: space-between;
}

.header-title {
	display: flex;
	align-items: center;
	gap: 16px;
	color: white;
}

.doc-title {
	font-weight: 500;
	text-align: center;
}

.edit-btn {
	display: flex;
	padding: 10px;
	border-radius: 8px;
	border: 1px solid #FFFFFF33;
	align-items: center;
	cursor: pointer;
}


.button-container {
	display: flex;
	flex-direction: row;
	gap: 1rem;
}

.share-btn {
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 12px;
	gap: 12px;
	border-radius: 8px;
	border: 1px solid #FFFFFF33;
}

.completed-wrappershare {
	display: flex;
	align-items: center;
	gap: 0.5em;
}


.stage-container {
	display: flex;
	flex-direction: column;
	justify-content: center;
	width: 100%;
	height: 100%;
	overflow: hidden;
	align-items: center;
}

.completed-section {
	display: flex;
	height: 100%;
	overflow: hidden;
	flex-direction: column;
	align-items: center;
}

.pr-plan-footer {
	position: sticky;
	bottom: 0;
	left: 0;
	right: 0;
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 1rem 2rem;
	background: #180914;
	border-top: 1px solid #FFFFFF33;
	color: white;
	z-index: 100;
}

.footer-left {
	display: flex;
	gap: 1em;
	align-items: center;
}

.delete-btn {
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 8px 12px 8px 12px;
	gap: 12px;
	border-radius: 8px;
	border: 1px solid #FFFFFF33;
	font-size: 14px;
}

.save-as-draft {
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 8px 24px 8px 24px;
	gap: 12px;
	border-radius: 8px;
	border: 1px solid #FFFFFF33;
}

.save-as-draft p {
	font-size: 14px;
}

.generate-btn {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 8px;
	padding: 8px 20px;
	border-radius: 8px;
	background: var(--primary-gradient);
}

.download-btn {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 8px;
	padding: 8px 20px;
	border-radius: 8px;
	background: var(--primary-gradient-with-black);
}

.generate-btn :disabled {
	background: var(--primary-gradient-with-black);
}

.continue-btn {
	display: flex;
	flex-direction: row;
	gap: 0.5rem;
}

.overlay {
	height: 100%;
	background: rgba(0, 0, 0, 0.6);
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	color: white;
	z-index: 9999;
}

.overlay :deep(.v-overlay__content) {
	display: flex;
	flex-direction: column !important;
	justify-content: center !important;
	align-items: center !important;
	gap: 1rem;
}
</style>
